import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Package, Loader2, AlertCircle, Gem, Edit3, Clock, Crown, Flame } from 'lucide-react'
import useCasesStore from '../store/useCasesStore'
import useAppStore from '../store/useAppStore'
import { formatMinutesLeft } from '../utils/formatters'
import SeedEditor, { resetSavedSeed } from '../components/SeedEditor'
import AnimatedButton from '../components/AnimatedButton'
import GameIcon from '../components/GameIcon'
import useAudioManager from '../hooks/useAudioManager'
import { raritySurfaceStyle } from '../utils/rarity'

const MAX_OPEN_COUNT = 100

const CaseDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { currentCase, loadCaseDetails, isLoading, error } = useCasesStore()
  const { balance, language, isGuest, openLogin } = useAppStore()
  
  // Инициализация Audio Manager
  const { playSound } = useAudioManager({
    pick: 'pick'
  })
  
  // Состояние для модалки редактора seed
  const [isSeedEditorOpen, setIsSeedEditorOpen] = useState(false)
  // Состояние загрузки изображения кейса
  const [isImageLoading, setIsImageLoading] = useState(true)
  // Состояние количества открытий кейса (1-100)
  const [openCount, setOpenCount] = useState(1)

  useEffect(() => {
    // Load case details from API
    loadCaseDetails(id)
    // Сбрасываем состояние загрузки изображения при смене кейса
    setIsImageLoading(true)
  }, [id, language, isGuest, loadCaseDetails])

  // Редирект на страницу всех кейсов, если кейс не найден (персональный кейс уже открыт/удалён)
  useEffect(() => {
    if (!isGuest && error === 'not_found_case') {
      navigate('/cases', { replace: true })
    }
  }, [error, isGuest, navigate])

  // Use currentCase from API response for all case data
  const caseItem = currentCase

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-[60vh] text-white flex items-center justify-center">
        <Loader2 className="animate-spin text-accent-primary" size={48} />
      </div>
    )
  }

  if (isGuest && error === 'not_found_case') {
    return <div className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center gap-5 px-6 text-center">
      <h1 className="text-2xl text-white">{t('guest.privateTitle')}</h1>
      <button type="button" onClick={openLogin} className="rounded-xl bg-accent-primary px-6 py-3 font-bold text-dark-primary">{t('guest.login')}</button>
    </div>
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-[60vh] bg-dark-primary text-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">
            {t('common.error')}
          </h2>
          <p className="text-text-secondary mb-4">{error}</p>
          <button 
            onClick={() => loadCaseDetails(id)}
            className="bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary px-6 py-2 rounded-lg font-semibold hover:opacity-90 transition-all"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  // Case not found - only show this if not loading and no currentCase
  if (!isLoading && !caseItem) {
    return (
      <div className="min-h-[60vh] bg-dark-primary text-white flex items-center justify-center">
        <div className="text-center">
          <Package size={64} className="mx-auto text-text-secondary mb-4" />
          <h2 className="text-xl font-semibold text-text-secondary">
            {t('cases.caseNotFound')}
          </h2>
        </div>
      </div>
    )
  }

  // Don't render main content if still loading or no case data
  if (isLoading || !caseItem) {
    return null
  }

  // Для приватных кейсов используем price_discounted, для обычных — price
  const isPrivate = caseItem?.is_private || false
  const effectivePrice = isPrivate ? (caseItem.price_discounted != null ? caseItem.price_discounted : caseItem.price) : caseItem.price
  const isExhausted = caseItem.opens_left === 0 || caseItem.minutes_left === 0

  const handleOpenCase = async () => {
    if (isGuest) return openLogin()
    // Отправляем цель в Яндекс.Метрику
    if (window.ym) {
      window.ym(104258304, 'reachGoal', 'open-case')
    }
    
    // Рассчитываем общую стоимость
    const totalCost = effectivePrice * openCount
    
    if (balance >= totalCost) {
      // Сбрасываем сохраненный токен перед открытием кейса
      resetSavedSeed()
      
      // Если открываем 1 кейс - переходим на обычную страницу с рулеткой
      if (openCount === 1) {
        navigate(`/cases/${id}/opening`, { 
          state: { allowOpening: true } 
        })
      } else {
        // Если открываем 2+ кейса - переходим на страницу множественного открытия
        navigate(`/cases/${id}/opening-multi`, { 
          state: { allowOpening: true, count: openCount } 
        })
      }
    } else {
      navigate('/payment')
    }
  }
  
  // Функция увеличения количества открытий
  const incrementCount = (amount = 1) => {
    if (openCount < MAX_OPEN_COUNT) {
      playSound('pick')
      setOpenCount(count => Math.min(MAX_OPEN_COUNT, count + amount))
    }
  }
  
  // Функция уменьшения количества открытий
  const decrementCount = () => {
    if (openCount > 1) {
      playSound('pick')
      setOpenCount(openCount - 1)
    }
  }

  // Get skins from currentCase (API response) or empty array
  const skins = currentCase?.skins || []
  
  // Сортируем скины по стоимости в кристаллах (от большей к меньшей)
  const sortedSkins = [...skins].sort((a, b) => (b.cost || b.price || 0) - (a.cost || a.price || 0))

  // Функция для сокращения состояния износа
  const shortenWear = (text) => {
    return text
      .replace(/\(Factory New\)/gi, 'FN')
      .replace(/\(Minimal Wear\)/gi, 'MW')
      .replace(/\(Field-Tested\)/gi, 'FT')
      .replace(/\(Well-Worn\)/gi, 'WW')
      .replace(/\(Battle-Scarred\)/gi, 'BS')
  }

  const controlsPanelContent = (
    <>
      {/* Мультиоткрытие — только для обычных кейсов */}
      {!isPrivate && (
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={decrementCount}
            disabled={openCount === 1}
            className={`w-10 h-10 rounded-lg font-bold text-xl transition-all ${
              openCount === 1
                ? 'bg-surface text-text-secondary/50 cursor-not-allowed'
                : 'bg-surface text-white hover:bg-surface/80 active:scale-95'
            }`}
          >
            −
          </button>

          <div className="flex items-center justify-center min-w-[100px] h-10 bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 border-2 border-accent-primary/50 rounded-lg">
            <span className="text-xl font-bold text-accent-primary">{openCount}</span>
          </div>

          {[1, 10].map(amount => (
            <button
              key={amount}
              type="button"
              onClick={() => incrementCount(amount)}
              disabled={openCount >= MAX_OPEN_COUNT}
              aria-label={`+${amount}`}
              title={`+${amount}`}
              className={`w-10 h-10 rounded-lg font-bold text-xl transition-all ${
                openCount >= MAX_OPEN_COUNT
                  ? 'bg-surface text-text-secondary/50 cursor-not-allowed'
                  : 'bg-surface text-white hover:bg-surface/80 active:scale-95'
              }`}
            >
              {amount === 1 ? '+' : '++'}
            </button>
          ))}
        </div>
      )}

      <div>
        {isExhausted ? (
          <button
            disabled
            className="w-full py-4 rounded-lg font-bold text-lg bg-surface text-text-secondary cursor-not-allowed"
          >
            {t('cases.caseExhausted')}
          </button>
        ) : isGuest ? (
          <AnimatedButton onClick={openLogin}>{t('guest.login')}</AnimatedButton>
        ) : balance >= (effectivePrice * openCount) ? (
          <AnimatedButton onClick={handleOpenCase} soundFeedback={openCount === 1}>
            <span className="flex items-center justify-center gap-2">
              {isPrivate && effectivePrice === 0
                ? t('cases.openFree')
                : openCount === 1
                  ? t('cases.openFor', { price: Number(effectivePrice).toLocaleString() })
                  : t('cases.openCountFor', { count: openCount, price: Number(effectivePrice * openCount).toLocaleString() })
              }
              {!(isPrivate && effectivePrice === 0) && <Gem size={20} />}
              {isPrivate && caseItem.discount > 0 && caseItem.price > effectivePrice && (
                <span className="flex items-center gap-0.5 text-sm text-dark-primary/60 line-through">
                  {openCount === 1
                    ? Number(caseItem.price).toLocaleString()
                    : Number(caseItem.price * openCount).toLocaleString()
                  }
                </span>
              )}
            </span>
          </AnimatedButton>
        ) : (
          <button
            onClick={handleOpenCase}
            className="w-full py-4 rounded-lg font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2 hover:opacity-90 transform hover:scale-[1.02] active:scale-[0.98] bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-blue-500/30"
          >
            {t('cases.topUpOn', { amount: Number((effectivePrice * openCount) - balance).toLocaleString() })}
            <Gem size={20} />
          </button>
        )}
      </div>

      <div className="text-center">
        <button
          onClick={() => {
            console.log('Opening seed editor...')
            setIsSeedEditorOpen(true)
          }}
          className="text-text-secondary hover:text-accent-primary transition-colors inline-flex items-center gap-2 text-sm"
        >
          <Edit3 size={14} />
          {t('seedEditor.editSeed')}
        </button>
      </div>
    </>
  )

  return (
    <div className=" text-white pb-[100px] lg:pb-0">
      {/* Header */}
      <div className="p-6 pb-0">
        <div className="flex items-center gap-4 mb-4 justify-center">
          <button 
            onClick={() => navigate('/cases')}
            className="p-2 rounded-lg bg-surface hover:bg-opacity-80 transition-all"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-accent-primary lg:text-4xl flex items-center gap-2">
            {caseItem.game && <GameIcon game={caseItem.game} size={24} />}
            {caseItem.name}
          </h1>
        </div>
        
      </div>

      {/* Case Preview */}
      <div className="lg:mx-auto lg:w-full lg:max-w-md">
        {/* Large Case Image - на всю ширину */}
        <div className="relative mb-6 w-full bg-gradient-to-br from-dark-primary to-surface lg:overflow-hidden lg:rounded-[32px] lg:border lg:border-white/10 lg:bg-gradient-to-br lg:from-surface/80 lg:via-dark-primary lg:to-surface/70 lg:p-5 lg:shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
          <div className="pointer-events-none absolute inset-x-10 top-4 hidden h-24 rounded-full bg-accent-primary/10 blur-3xl lg:block" />
          <div className="pointer-events-none absolute inset-x-16 bottom-0 hidden h-20 rounded-full bg-white/5 blur-3xl lg:block" />
          {/* Скелетон с фиксированным aspect ratio */}
          {isImageLoading && (
            <div className="aspect-square w-full bg-black animate-pulse lg:rounded-2xl" />
          )}
          <img 
            src={caseItem.img} 
            alt={caseItem.name}
            className={`w-full h-auto object-cover lg:rounded-2xl lg:border lg:border-white/10 lg:bg-black/20 lg:shadow-[0_20px_50px_rgba(0,0,0,0.35)] ${isImageLoading ? 'absolute inset-0 opacity-0' : 'opacity-100'} `}
            onLoad={() => setIsImageLoading(false)}
            onError={() => setIsImageLoading(false)}
          />
        </div>
        
        {/* Case Info */}
        <div className="px-6 mb-6 lg:px-0">
          <p className="text-center text-white/90 text-base leading-relaxed">
            {caseItem.desc}
          </p>

          {/* Бейджи приватности и лимитов */}
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {isPrivate && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold bg-gradient-to-r from-amber-400/20 to-orange-500/20 text-amber-300 border border-amber-500/30">
                <Crown size={12} />
                {t('cases.private')}
              </span>
            )}
            {isPrivate && caseItem.discount > 0 && (
              <span className="px-2 py-1 rounded text-xs font-bold bg-accent-primary/20 text-accent-primary border border-accent-primary/30">
                -{caseItem.discount}%
              </span>
            )}
            {caseItem.opens_left != null && caseItem.opens_left > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold bg-red-500/20 text-red-300 border border-red-500/30">
                <Flame size={12} />
                {t('cases.opensLeft', { count: caseItem.opens_left })}
              </span>
            )}
            {caseItem.minutes_left != null && caseItem.minutes_left > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold bg-orange-500/20 text-orange-300 border border-orange-500/30">
                <Clock size={12} />
                {formatMinutesLeft(caseItem.minutes_left, t)}
              </span>
            )}
          </div>
        </div>

        <div className="hidden lg:block lg:mb-8">
          <div className="space-y-3">
            {controlsPanelContent}
          </div>
        </div>
      </div>

      <div className="px-6">
        {/* Skins List */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">{t('cases.possibleItems')}</h3>
          <div className="grid grid-cols-3 gap-3 lg:grid-cols-6">
            {sortedSkins.map((skin, index) => (
              <div
                key={index}
                className="rounded-lg p-2 border relative flex flex-col bg-surface border-white border-opacity-10"
                style={raritySurfaceStyle(skin)}
              >
                <div className="aspect-square rounded mb-2 overflow-hidden relative z-10 flex items-center justify-center p-2">
                  <img
                    src={skin.img}
                    alt={skin.name}
                    className="w-auto h-auto max-w-full max-h-full object-contain"
                  />
                </div>
                  
                  {/* Название скина */}
                  <div className="text-sm font-medium text-center leading-tight relative z-10 flex-grow">
                    {skin.name.includes('|') ? (
                      <>
                        <div className="text-white">
                          {skin.name.split('|')[0].trim()}
                        </div>
                        <div className="text-text-secondary text-xs mt-1">
                          {shortenWear(skin.name.split('|')[1].trim())}
                        </div>
                      </>
                    ) : (
                      <div className="text-white">
                        {shortenWear(skin.name)}
                      </div>
                    )}
                  </div>
                  
                  {/* Стоимость в кристаллах и шанс */}
                  <div className="mt-2 text-center relative z-10 space-y-1">
                    {skin.cost != null && skin.cost > 0 && (
                      <div className="flex items-center justify-center gap-1 text-xs text-accent-primary font-bold">
                        <Gem size={10} />
                        <span>{Number(skin.cost).toLocaleString()}</span>
                      </div>
                    )}
                    {skin.chance != null && (
                      <div className="text-[10px] text-text-secondary">
                        {skin.chance}%
                      </div>
                    )}
                    {skin.x !== undefined && (
                      <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ${
                        skin.x < 1
                          ? 'bg-gray-600 text-gray-300'
                          : 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
                      }`}>
                        x{skin.x % 1 === 0 ? skin.x : skin.x.toFixed(1)}
                      </span>
                    )}
                  </div>
                </div>
            ))}
          </div>
        </div>
      </div>

      {/* Фиксированная панель управления внизу */}
      <div className="fixed bottom-0 left-0 right-0 bg-dark-primary border-t border-white/10 z-50 lg:hidden">
        <div className="px-6 py-4 space-y-3">
          {controlsPanelContent}
        </div>
      </div>

      {/* Модальное окно редактора seed */}
      <SeedEditor 
        isOpen={isSeedEditorOpen} 
        onClose={() => setIsSeedEditorOpen(false)} 
      />
    </div>
  )
}

export default CaseDetail
